import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import cityExplorerPublicShareService, { clearShareToken } from '../../services/cityExplorerPublicShareService';
import { CityExplorerDataProvider } from './CityExplorerDataContext';
import ExplorerView from './ExplorerView';
import { analysisTitle } from './LocationLanding';

const SharedLocationExplorer = () => {
  const { shareId, categoryId, fileId, resourceId } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [passwordGate, setPasswordGate] = useState(null);
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const liveData = useMemo(() => cityExplorerPublicShareService.liveDataClient(shareId), [shareId]);

  const loadShare = useCallback(async () => {
    if (!shareId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cityExplorerPublicShareService.getShare(shareId);
      if (data.requiresPassword) {
        setPasswordGate({
          locationName: data.locationName,
          label: data.label,
        });
        setLocation(null);
        return;
      }
      setPasswordGate(null);
      setLocation(data.location);
    } catch (err) {
      setLocation(null);
      setPasswordGate(null);
      setError(err.message || 'This link is no longer available');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    loadShare();
  }, [loadShare]);

  const handleUnlock = async (event) => {
    event.preventDefault();
    setUnlocking(true);
    setUnlockError(null);
    try {
      await cityExplorerPublicShareService.unlock(shareId, password);
      setPassword('');
      await loadShare();
    } catch (err) {
      if (err.status === 404) {
        clearShareToken(shareId);
        setPasswordGate(null);
        setError(err.message || 'This link is no longer available');
      } else {
        setUnlockError(err.message || 'Incorrect password');
      }
    } finally {
      setUnlocking(false);
    }
  };

  const fetchMapUrl = useCallback(
    (mapCategoryId, mapFileId, disposition = 'inline') =>
      cityExplorerPublicShareService.getMapUrl(shareId, mapCategoryId, mapFileId, disposition),
    [shareId]
  );

  const fetchResourceUrl = useCallback(
    (mapResourceId, disposition = 'inline') =>
      cityExplorerPublicShareService.getResourceUrl(shareId, mapResourceId, disposition),
    [shareId]
  );

  if (passwordGate) {
    return (
      <Box
        data-testid="share-password-gate"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'grey.100',
        }}
      >
        <Box sx={{ position: 'relative', maxWidth: 420, width: '100%', pt: 6 }}>
          <Box
            component="img"
            src="/images/edc_logo_circle.png"
            alt="Education Data Collective"
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 88,
              height: 88,
              bgcolor: '#fff',
              borderRadius: '50%',
              p: 0.5,
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 1,
            }}
          />
          <Paper sx={{ p: 4, pt: 7 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {analysisTitle(passwordGate.locationName)}
            </Typography>
            {passwordGate.label && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {passwordGate.label}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This link is password protected.
            </Typography>
            <Box component="form" onSubmit={handleUnlock}>
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                fullWidth
                autoFocus
                error={Boolean(unlockError)}
                helperText={unlockError}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={unlocking || !password}
                sx={{ mt: 2 }}
              >
                {unlocking ? 'Opening…' : 'Open'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (loading && !location) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 720 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <CityExplorerDataProvider value={liveData}>
      <ExplorerView
        location={location}
        loadingLocation={loading}
        locationError={null}
        basePath={`/share/${shareId}`}
        categoryId={categoryId}
        fileId={fileId}
        resourceId={resourceId}
        fetchMapUrl={fetchMapUrl}
        fetchResourceUrl={fetchResourceUrl}
        footerOffsetPx={0}
        showAllLocationsLink={false}
      />
    </CityExplorerDataProvider>
  );
};

export default SharedLocationExplorer;
